import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface FTPPoint {
  date: string;
  ftp: number;
}

interface FTPTrendChartProps {
  data: Record<string, unknown>;
}

export function FTPTrendChart({ data }: FTPTrendChartProps) {
  const chartData = useMemo(() => {
    const ftpHistory = data.ftp_history as Array<{ date: string; value: number }> | undefined;
    if (!ftpHistory?.length) return [];
    return ftpHistory.map(
      (p): FTPPoint => ({ date: p.date, ftp: Math.round(p.value) }),
    );
  }, [data]);

  const currentFtp = typeof data.current_ftp === "number" ? Math.round(data.current_ftp as number) : null;
  const wattsPerKg = typeof data.watts_per_kg === "number" ? (data.watts_per_kg as number).toFixed(2) : null;
  const ftpTrend = data.ftp_trend as { direction?: string; slope?: number } | undefined;

  if (!chartData.length && !currentFtp) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">FTP Trend</h3>
        <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
          No FTP data yet. Record FTP benchmarks to track your power trend.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">FTP Trend</h3>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Current FTP</div>
          <div className="text-lg font-bold tabular-nums text-foreground">{currentFtp ?? "—"}W</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">W/kg</div>
          <div className="text-lg font-bold tabular-nums text-foreground">{wattsPerKg ?? "—"}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Trend</div>
          <div className={`text-lg font-bold ${ftpTrend?.direction === "improving" ? "text-emerald-400" : ftpTrend?.direction === "declining" ? "text-red-400" : "text-muted-foreground"}`}>
            {ftpTrend?.direction === "improving" ? "↑" : ftpTrend?.direction === "declining" ? "↓" : "→"}
          </div>
        </div>
      </div>

      {chartData.length > 0 && (
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={40} domain={["auto", "auto"]} />
              <Tooltip
                contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }}
                formatter={(value: number) => [`${value}W`, "FTP"]}
              />
              {currentFtp && (
                <ReferenceLine y={currentFtp} stroke="hsl(35 85% 55%)" strokeDasharray="4 2" opacity={0.5} />
              )}
              <Line type="monotone" dataKey="ftp" stroke="hsl(35 85% 55%)" strokeWidth={2} dot={{ r: 3, fill: "hsl(35 85% 55%)" }} name="FTP" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
